import { useQuery, useMutation } from '@apollo/client/react';
import { logError } from 'fergy-core-react-logging';
import { useState, type FunctionComponent } from 'react';
import {
	type CartDrawerRecommendedProductsQuery,
	type CartDrawerRecommendedProductsQueryVariables,
	type AddProductToCartMutation,
	type AddProductToCartMutationVariables
} from '../../../../../__generated__/graphql-client-types';
import { NEW_ATC_MUTATION_OPTIONS } from '../../../../../hooks/add-to-cart/add-to-cart.hooks';
import { useNavigation } from '../../../../../hooks/navigation/navigation.hooks';
import { useNotification } from '../../../../../hooks/notification/notification.hooks';
import { ADD_PRODUCT_TO_CART } from '../../../../../queries/cart/add-to-cart.queries';
import { CART_DRAWER_RECS } from '../../../../../queries/product/cart-drawer/cart-drawer.queries';
import { type CartDrawerItem } from '../../../../../types/product.types';
import { StyledButton } from '../../../../buttons';
import { DropdownMenu, DropdownMenuItem } from '../../../../common-components/dropdown-menu/dropdown-menu.component';
import { StyledLink } from '../../../../common-components/link/styled-link.component';
import { CloudinaryImage } from '../../../../images/cloudinary-image/cloudinary-image.component';
import { QuantityInput } from '../../../../inputs/quantity-input/quantity-input.component';
import { ProductPrice } from '../../../product-price/product-price.component';
import { ProductStock } from '../../../product-stock/product-stock.component';

type CartDrawerRecItemProps = {
	rec: CartDrawerItem;
};

const CartDrawerRecItem: FunctionComponent<CartDrawerRecItemProps> = ({ rec }) => {
	const [quantity, setQuantity] = useState<number | undefined>(1);
	// resolver places the correct variant at index 0 before sending to client
	const [selectedVariantId, setSelectedVariantId] = useState<string | null>(() => rec.variants?.[0]?.id ?? null);
	const [addProductToCart] = useMutation<AddProductToCartMutation, AddProductToCartMutationVariables>(
		ADD_PRODUCT_TO_CART,
		NEW_ATC_MUTATION_OPTIONS
	);
	const { notifyDanger, notifySuccess } = useNotification();

	const navigate = useNavigation();

	const currentVariant = rec.variants?.find((v) => v.id === selectedVariantId) ?? rec.variants?.[0] ?? null;
	const itemClasses = `ba b--theme-grey-light br2 pa3 mb3`;

	const handleAddToCart = () => {
		if (currentVariant) {
			addProductToCart({
				variables: { input: { variantId: Number(currentVariant.id), quantity: quantity ?? 1 }, subItems: [] }
			})
				.then((payload) => {
					if (Number(payload.data?.addProductToCart?.newItemIds.length) > 0) {
						notifySuccess('Item successfully added to cart!');
					}
				})
				.catch((error) => {
					logError('Error adding product to cart:', error);
					notifyDanger('Error adding product to cart');
				});
		}
	};

	// A rec with no variants has nothing to render, and every field below dereferences currentVariant.
	if (!currentVariant) {
		return null;
	}

	return (
		<li className={itemClasses} data-testid="cart-drawer-recommendation-item" data-automation="cart-drawer-recommendation-item">
			<div className="flex flex-column flex-row-ns">
				<div className="center mr3-ns">
					<CloudinaryImage
						className="mr3"
						publicID={currentVariant.image.id}
						description={currentVariant.image.description}
						options={{ width: 128, height: 124, crop: 'lpad' }}
						useDimensions={false}
						bypassLazyLoad={true}
						data-automation={`cart-drawer-recommendation-item-image-${currentVariant.id}`}
					/>
				</div>
				<div className="flex flex-column pl3-ns w-100">
					<span className="tc2-title theme-black mb2">
						{rec.brandName && (
							<strong className="pr1 f-normal" data-automation={`cart-drawer-recommendation-item-brand-${currentVariant.id}`}>
								{rec.brandName}
							</strong>
						)}
						{rec.title}
					</span>
					<div className={`flex flex-row flex-wrap items-center mt2 mb1 w-100`}>
						<span>Finish: </span>
						<span className="ml2 font-medium" data-automation={`finish-name-${currentVariant.id}`}>
							{currentVariant.name}
						</span>

						<div className="flex flex-row flex-wrap items-center justify-start">
							<span className="theme-emphasis mh1">-</span>
							<ProductStock
								data-automation={`cart-drawer-recommendation-item-stock-${currentVariant.id}`}
								count={currentVariant.stock}
								isMadeToOrder={false}
								brand={rec.brandName}
							/>
						</div>
					</div>
					{rec.variants.length > 1 && (
						<DropdownMenu
							shouldCloseOnChoice={true}
							title={currentVariant.name}
							initialSelectedIndex={0}
							onChooseItem={(selectedIndex: number) => setSelectedVariantId(rec.variants[selectedIndex].id)}
							data-testid="variant-dropdown"
							data-automation={`variant-dropdown-${currentVariant.id}`}>
							{rec.variants.map((variant, index) => (
								<DropdownMenuItem key={`${variant.name}-${variant.id}-${index}`}>
									<span data-automation={`variant-name-${variant.id}`}>{variant.name}</span>
								</DropdownMenuItem>
							))}
						</DropdownMenu>
					)}

					<span className="flex items-center justify-start mt1">
						Price:
						<ProductPrice
							data-automation={`cart-drawer-recommendation-item-price-${currentVariant.id}`}
							current={currentVariant.price}
							className="ml2"
						/>
					</span>
					<div className="mt1 w4">
						<QuantityInput
							data-automation={`cart-drawer-recommendation-item-quantity-${currentVariant.id}`}
							quantity={quantity}
							setQuantity={setQuantity}
							automationHook="product-option-quantity"
						/>{' '}
					</div>
				</div>
			</div>
			<div className="w-100">
				<div className="flex flex-column flex-row-ns flex-nowrap items-center justify-end mt3">
					<StyledLink
						data-automation={`cart-drawer-recommendation-item-link-${currentVariant.id}`}
						url={currentVariant.url}
						className="f6 mb3 mb0-ns"
						openInNewTab={true}>
						View Full Product Details
					</StyledLink>

					<span className="br b--theme-grey-light mh3 h2 dn db-ns" />
					{rec.isConfigurable ? (
						<StyledButton
							data-automation={`cart-drawer-recommendation-item-configure-${currentVariant.id}`}
							onClick={() => navigate(currentVariant.url)}
							buttonStyle={'PRIMARY'}
							size={'SMALL'}>
							Configure
						</StyledButton>
					) : (
						<StyledButton
							data-automation={`cart-drawer-recommendation-item-add-to-cart-${currentVariant.id}`}
							onClick={handleAddToCart}
							buttonStyle={'PRIMARY'}
							size={'SMALL'}>
							Add To Cart
						</StyledButton>
					)}
				</div>
			</div>
		</li>
	);
};

export type CartDrawerRecommendationsProps = {
	familyId: number;
	variantId: number;
};

export const CartDrawerRecommendations: FunctionComponent<CartDrawerRecommendationsProps> = ({ familyId, variantId }) => {
	const { data } = useQuery<CartDrawerRecommendedProductsQuery, CartDrawerRecommendedProductsQueryVariables>(CART_DRAWER_RECS, {
		variables: {
			familyId,
			variantId,
			path: window.location.pathname
		},
		fetchPolicy: 'no-cache'
	});
	const productFamily = data?.productFamily?.__typename === 'ProductFamily' ? data.productFamily : undefined;
	const cartDrawerRecsGroups =
		productFamily && 'cartDrawerRecommendedProducts' in productFamily ? productFamily.cartDrawerRecommendedProducts : [];
	const compatibleItems = cartDrawerRecsGroups.filter((group) => group.items.some((item) => item.origin === 'RECOMMENDED'));
	const mayWeSuggestItems = cartDrawerRecsGroups.filter((group) => group.items.some((item) => item.origin === 'CURATED'));

	const listClasses = 'list flex flex-column items-stretch justify-start ma0 pa0';

	return (
		cartDrawerRecsGroups.length > 0 && (
			<section className="flex flex-column items-stretch justify-between w-100 mb6">
				{compatibleItems.length > 0 && (
					<>
						<h4 className="mt0 mb3 f4">Compatible Items</h4>
						{compatibleItems.map((group) => (
							<div key={group.groupTitle} className="mb3">
								<h5 className="mt0 mb2 f5-ns f6">{group.groupTitle}</h5>
								<ul className={listClasses} id="cart-recently-updated-recs" data-testid="cart-items-recently-updated-recs">
									{group.items.map((rec) => (
										<CartDrawerRecItem rec={rec} key={rec.familyId} />
									))}
								</ul>
							</div>
						))}
					</>
				)}
				{mayWeSuggestItems.length > 0 && (
					<>
						<h4 className="mt0 mb3 f4">May We Suggest</h4>
						<ul className={listClasses} id="cart-may-we-suggest-recs" data-testid="cart-items-may-we-suggest-recs">
							{mayWeSuggestItems
								.flatMap((group) => group.items)
								.map((rec) => (
									<CartDrawerRecItem rec={rec} key={rec.familyId} />
								))}
						</ul>
					</>
				)}
			</section>
		)
	);
};
